import { TravelFile } from '../travelFiles/oldModelTypes';
import { getToFixedNumber, taytanFormatNumberToString } from '../../utils/mathUtils';
import { resolveDocumentAmountForTable } from '../../utils/currencyMath';
import { TotalAccountingAmounts } from '../../routes/Reservations/TravelFile/TravelFileHomePage/TravelFileHomePage';
import { TravelFileTypes } from '../travelFiles/constants/travelFileTypesOptions';

import { Voucher } from './model';
import React from 'react';

export type DocumentTypes =
    | 'Voucher'
    | 'Refund'
    | 'Invoice'
    | 'Tax Invoice'
    | 'Agent Invoice'
    | 'Receipt'
    | 'Transfer Receipt'
    | 'Credit Note'
    | 'Debit Note'
    | 'Payment Order (Remittance)'
    | 'Payment Order Remittance'
    | 'Payment Requisition'
    | 'Statement'
    | 'Deposit'
    | 'Withdrawal'
    | 'Extended Refund'
    | '';

export type DocumentTypesKeys =
    | 'VOUCHER'
    | 'REFUND'
    | 'INVOICE'
    | 'TAX_INVOICE'
    | 'AGENT_INVOICE'
    | 'RECEIPT'
    | 'TRANSFER_RECEIPT'
    | 'CREDIT_DEBIT'
    | 'DEBIT_CREDIT'
    | 'REMITTANCE'
    | 'PRQ'
    | 'DEPOSIT'
    | 'WITHDRAWAL'
    | 'STATEMENT'
    | 'EXTENDED_REFUND';
export const documentTypesEnum: { [key in DocumentTypesKeys]: DocumentTypes } = {
    VOUCHER: 'Voucher',
    REFUND: 'Refund',
    INVOICE: 'Invoice',
    TAX_INVOICE: 'Tax Invoice',
    AGENT_INVOICE: 'Agent Invoice',
    RECEIPT: 'Receipt',
    TRANSFER_RECEIPT: 'Transfer Receipt',
    CREDIT_DEBIT: 'Credit Note',
    DEBIT_CREDIT: 'Debit Note',
    REMITTANCE: 'Payment Order (Remittance)',
    PRQ: 'Payment Requisition',
    STATEMENT: 'Statement',
    DEPOSIT: 'Deposit',
    WITHDRAWAL: 'Withdrawal',
    EXTENDED_REFUND: 'Extended Refund'
};
export const documentTypesOptions = Object.values(documentTypesEnum) as DocumentTypes[];

export type DocumentTypesInitials =
    | 'VCH'
    | 'REF'
    | 'INV'
    | 'TXI'
    | 'ACI'
    | 'RCT'
    | 'TRC'
    | 'C/D'
    | 'D/C'
    | 'RMT'
    | 'PRQ'
    | 'STT'
    | 'DEP'
    | 'WD'
    | '';
export const documentTypesInitialsEnum: { [key in DocumentTypes]: DocumentTypesInitials } = {
    Voucher: 'VCH',
    Refund: 'REF',
    Invoice: 'INV',
    'Tax Invoice': 'TXI',
    'Agent Invoice': 'ACI',
    Receipt: 'RCT',
    'Transfer Receipt': 'TRC',
    'Credit Note': 'C/D',
    'Debit Note': 'D/C',
    'Payment Order (Remittance)': 'RMT',
    'Payment Order Remittance': 'RMT',
    'Payment Requisition': 'PRQ',
    Statement: 'STT',
    Deposit: 'DEP',
    Withdrawal: 'WD',
    'Extended Refund': 'REF',
    '': ''
};

export type NotBalancedVoucher = {
    message: string;
    sourceId: string;
    sourceType: string;
    sourceNumber: string;
    targetId: string;
    targetType: string;
    targetNumber: string;
};
export const NO_DOCUMENTS_TO_BALANCE = 'No Documents to balance';
export const SOURCE_ALREADY_BALANCED = 'Source already balanced';

export type VoucherCreditOrDebitTypes = 'credit' | 'debit' | '';
type VoucherCreditOrDebitKeys = 'CREDIT' | 'DEBIT';
export const voucherCreditOrDebitTypesEnum: {
    [key in VoucherCreditOrDebitKeys]: VoucherCreditOrDebitTypes;
} = {
    CREDIT: 'credit',
    DEBIT: 'debit'
};

/**
 * A document's `sourceId`/`targetId` are POLYMORPHIC: the matching
 * `sourceType`/`targetType` decides what the number points at. Only 'client'
 * and 'group' denote a travel file — 'account', 'generalaccount', 'supplier'
 * and 'agent' are separate id spaces that reuse the same integers.
 *
 * Comparing the id alone therefore matches unrelated documents. General and
 * bank accounts are numbered 1-99, and every imported table numbers its rows
 * `g_id + 1,000,000,000`, so travelagencyaccounts and supplieraccounts collide
 * with travelfile head-on.
 *
 * `undefined` is treated as a travel file so callers that have no type to hand
 * keep their previous behaviour rather than silently losing every row.
 */
export const isTravelFileSide = (type: Voucher['sourceType'] | undefined): boolean =>
    type === undefined || type === 'client' || type === 'group';

/** True when this document's source side is the given travel file — type included. */
export const isSourceThisTravelFile = (props: {
    sourceId: Voucher['sourceId'];
    sourceType?: Voucher['sourceType'];
    travelFileId: TravelFile['_id'];
}): boolean => {
    const { sourceId, sourceType, travelFileId } = props;
    return sourceId === travelFileId && isTravelFileSide(sourceType);
};

export const getVoucherTypeCreditOrDebit = (props: {
    type: Voucher['type'];
    sourceId: Voucher['sourceId'];
    sourceType?: Voucher['sourceType'];
    travelFileId: TravelFile['_id'];
}): VoucherCreditOrDebitTypes => {
    // @ts-ignore
    const { type, sourceId, sourceType, travelFileId } = props;
    const sourceIsThisTravelFile = isSourceThisTravelFile({ sourceId, sourceType, travelFileId });

    switch (type) {
        // Per TB: Invoice credits the TF balance, PRQ debits it
        case documentTypesEnum.INVOICE:
        case documentTypesEnum.REMITTANCE:
        case documentTypesEnum.AGENT_INVOICE:
        case documentTypesEnum.TRANSFER_RECEIPT:
        case documentTypesEnum.RECEIPT: {
            return voucherCreditOrDebitTypesEnum.CREDIT;
        }
        case documentTypesEnum.TAX_INVOICE:
        case documentTypesEnum.PRQ: {
            return voucherCreditOrDebitTypesEnum.DEBIT;
        }
        case documentTypesEnum.VOUCHER:
        case documentTypesEnum.REFUND:
        case documentTypesEnum.EXTENDED_REFUND:
        case documentTypesEnum.WITHDRAWAL: {
            if (sourceIsThisTravelFile) {
                return voucherCreditOrDebitTypesEnum.DEBIT;
            } else {
                return voucherCreditOrDebitTypesEnum.CREDIT;
            }
        }
        // Credit/Debit notes: the source TF sees it as Debit, the target TF sees it as Credit
        case documentTypesEnum.DEBIT_CREDIT:
        case documentTypesEnum.CREDIT_DEBIT: {
            if (sourceIsThisTravelFile) {
                return voucherCreditOrDebitTypesEnum.DEBIT;
            }
            return voucherCreditOrDebitTypesEnum.CREDIT;
        }
        default:
            return '';
    }
};

export const voucherTypesBackgroundColorEnum = {
    [documentTypesEnum.RECEIPT]: '#327355',
    [documentTypesEnum.TRANSFER_RECEIPT]: '#327355',
    [documentTypesEnum.INVOICE]: '#265C4B',
    [documentTypesEnum.TAX_INVOICE]: '#007566',
    [documentTypesEnum.AGENT_INVOICE]: '#146551',
    [documentTypesEnum.VOUCHER]: '#802922',
    [documentTypesEnum.CREDIT_DEBIT]: '#A6039B',
    [documentTypesEnum.DEBIT_CREDIT]: '#A6039B',
    [documentTypesEnum.REFUND]: '#802922',
    [documentTypesEnum.REMITTANCE]: '#FF6B1A',
    [documentTypesEnum.PRQ]: '#B33F00'
};

// Brighter variants of the type colors for dark mode — the light-mode hues
// above are deep/saturated (designed for a white background) and read as
// "dark on dark" in the dark theme. These keep each type's identity while
// lifting luminance enough to stay legible on the dark table surface.
export const voucherTypesBackgroundColorDarkEnum = {
    [documentTypesEnum.RECEIPT]: '#5cc9a0',
    [documentTypesEnum.TRANSFER_RECEIPT]: '#5cc9a0',
    [documentTypesEnum.INVOICE]: '#5fc9ab',
    [documentTypesEnum.TAX_INVOICE]: '#33c9b5',
    [documentTypesEnum.AGENT_INVOICE]: '#4fc79b',
    [documentTypesEnum.VOUCHER]: '#f0876f',
    [documentTypesEnum.CREDIT_DEBIT]: '#e57ad9',
    [documentTypesEnum.DEBIT_CREDIT]: '#e57ad9',
    [documentTypesEnum.REFUND]: '#f0876f',
    [documentTypesEnum.REMITTANCE]: '#ff9d5c',
    [documentTypesEnum.PRQ]: '#ff8c4d'
};

// Map each type to a CSS variable (defined in base.scss, light + dark). Using
// a CSS var — instead of a JS light/dark pick — means grid cells re-resolve the
// colour live on theme change even when ag-grid columnDefs are frozen.
export const voucherTypeColorVarEnum = {
    [documentTypesEnum.RECEIPT]: 'var(--vt-receipt, #327355)',
    [documentTypesEnum.TRANSFER_RECEIPT]: 'var(--vt-receipt, #327355)',
    [documentTypesEnum.INVOICE]: 'var(--vt-invoice, #265c4b)',
    [documentTypesEnum.TAX_INVOICE]: 'var(--vt-tax-invoice, #007566)',
    [documentTypesEnum.AGENT_INVOICE]: 'var(--vt-agent-invoice, #146551)',
    [documentTypesEnum.VOUCHER]: 'var(--vt-voucher, #802922)',
    [documentTypesEnum.CREDIT_DEBIT]: 'var(--vt-credit-debit, #a6039b)',
    [documentTypesEnum.DEBIT_CREDIT]: 'var(--vt-credit-debit, #a6039b)',
    [documentTypesEnum.REFUND]: 'var(--vt-voucher, #802922)',
    [documentTypesEnum.REMITTANCE]: 'var(--vt-remittance, #ff6b1a)',
    [documentTypesEnum.PRQ]: 'var(--vt-prq, #b33f00)'
};

// Resolve a voucher/document type's display colour as a theme-aware CSS var.
// (Second arg kept for backward-compat; ignored.)
export const getVoucherTypeColor = (type: string, _isDark?: boolean): string =>
    voucherTypeColorVarEnum[type] || voucherTypesBackgroundColorEnum[type];

export type GetTravelFileTotalAccountingRes = Array<{
    travelFileId: string;
    travelFileType: TravelFileTypes;
    totalAccounting: TotalAccountingAmounts;
}>;

export const getAccountingTotalAmounts = (props: {
    vouchers: Voucher[];
    relatedServiceId: TravelFile['relatedServiceId'];
    travelFileId: TravelFile['_id'];
    travelFileCurrencyId: string;
    /** The client's local (ILS) and primary (USD) currency ids. Without them
     *  every document converts by the legacy arithmetic, which is only correct
     *  when the travel file is itself in the primary currency — see
     *  `convertVoucherAmountToCurrency`. */
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
}): TotalAccountingAmounts => {
    const {
        vouchers,
        relatedServiceId,
        travelFileId,
        travelFileCurrencyId,
        localCurrencyId,
        primaryCurrencyId
    } = props;

    const filteredVouchersOnlyForCount = vouchers.filter(
        (voucher) =>
            voucher.status !== voucherStatusesEnum.CANCEL &&
            isVoucherCountInAccountingSection(voucher, relatedServiceId).isCount
    );
    const totalAmount = getTotalVouchersAmountByVoucherRate({
        vouchers: filteredVouchersOnlyForCount,
        travelFileCurrencyId,
        localCurrencyId,
        primaryCurrencyId
    });
    const totalCredit: number = getTotalVouchersAmountByVoucherRate({
        vouchers: filteredVouchersOnlyForCount.filter((voucher) => {
            return (
                getVoucherTypeCreditOrDebit({
                    type: voucher.type,
                    sourceId: voucher.sourceId,
                    sourceType: voucher.sourceType,
                    travelFileId
                }) === voucherCreditOrDebitTypesEnum.CREDIT
            );
        }),
        travelFileCurrencyId,
        localCurrencyId,
        primaryCurrencyId
    });

    // TODO: Old code. Remove or comment in
    // const totalDebit: number = getTotalVouchersAmountByVoucherRateDebit({
    //     vouchers: filteredVouchersOnlyForCount.filter((voucher: Voucher) => {
    //         console.log("vocuher1", voucher)
    //         return (
    //             getVoucherTypeCreditOrDebit({
    //                 // type: voucher.type,
    //                 type: voucher.sourceId === travelFileId ? documentTypesEnum.DEBIT_CREDIT : voucher.type,
    //                 sourceId: voucher.sourceId,
    //                 travelFileId: voucher.travelFileId || travelFileId
    //             }) === voucherCreditOrDebitTypesEnum.DEBIT
    //         );
    //     }),
    //     travelFileCurrencyId
    // });
    const totalDebit: number = getTotalVouchersAmountByVoucherRate({
        vouchers: filteredVouchersOnlyForCount.filter(
            (voucher: Voucher) =>
                getVoucherTypeCreditOrDebit({
                    type: voucher.type,
                    sourceId: voucher.sourceId,
                    sourceType: voucher.sourceType,
                    travelFileId
                }) === voucherCreditOrDebitTypesEnum.DEBIT
        ),
        travelFileCurrencyId,
        localCurrencyId,
        primaryCurrencyId
    });

    return {
        totalAmount,
        totalCredit,
        totalDebit
    };
};

export const getAccountingTotalInvoices = (props: {
    vouchers: Voucher[];
    relatedServiceId: TravelFile['relatedServiceId'];
    travelFileCurrencyId: string;
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
}): { totalAmount: number; totalAgentCommission: number } => {
    const { vouchers, relatedServiceId, travelFileCurrencyId, localCurrencyId, primaryCurrencyId } =
        props;
    const filteredVouchersOnlyForCount = vouchers.filter(
        (voucher) =>
            voucher.status !== voucherStatusesEnum.CANCEL &&
            isVoucherCountInAccountingSection(voucher, relatedServiceId).isCount
    );
    const filteredInvoicesFromVouchers = filteredVouchersOnlyForCount.filter(
        (voucher) => voucher.type === documentTypesEnum.INVOICE
    );
    const totalAmount = getTotalVouchersAmountByVoucherRate({
        vouchers: filteredInvoicesFromVouchers,
        travelFileCurrencyId,
        localCurrencyId,
        primaryCurrencyId
    });
    const totalAgentCommission = getTotalInvoicesAgentCommissionAmountByVoucherRate({
        vouchers: filteredInvoicesFromVouchers,
        travelFileCurrencyId,
        localCurrencyId,
        primaryCurrencyId
    });
    return {
        totalAmount,
        totalAgentCommission
    };
};

export const isVoucherCountInAccountingSection = (
    voucher: Voucher,
    relatedServiceId?: TravelFile['relatedServiceId']
): { isCount: boolean; text: string } => {
    if (voucher.status === voucherStatusesEnum.CANCEL) {
        return {
            isCount: false,
            text: 'Canceled'
        };
    }
    switch (voucher.type) {
        // TODO: check this logic - unclear and incorrect
        case documentTypesEnum.TAX_INVOICE: {
            if (relatedServiceId) {
                return {
                    isCount: false,
                    text: ''
                };
            } else {
                // if no relatedServiceId it means that there is no related group docket, so vouchers will count
                // because there is no option to balance between group and client travel files
                return {
                    isCount: true,
                    text: '' //TODO: add the reason the tax invoice was issued (voucher or transaction fee)
                };
            }
        }
        case documentTypesEnum.REFUND:
        case documentTypesEnum.EXTENDED_REFUND: {
            if (relatedServiceId) {
                return {
                    isCount: false,
                    text: ''
                };
            } else {
                // Per TB: "PRQ {number} BSP For: {passenger}" or just supplier name
                const refSupplier = voucher.info?.supplierName || '';
                const refBeneficiary = voucher.info?.for || '';
                return {
                    isCount: true,
                    text: refBeneficiary ? `${refSupplier} For: ${refBeneficiary}` : refSupplier
                };
            }
        }
        case documentTypesEnum.PRQ: {
            if (voucher.info?.remittanceNumber || voucher.info?.coveredBy?.length !== 0) {
                return {
                    isCount: false,
                    text: ''
                };
            } else {
                // Per TB: "MOi-0101" or beneficiary
                const prqFor = voucher.info?.for || '';
                return {
                    isCount: true,
                    text: prqFor || ''
                };
            }
        }
        case documentTypesEnum.REMITTANCE: {
            // Per TB: "BRF {number} For: Refund to {passenger}" or supplier info
            const rmtRef = voucher.info?.paymentMethodObject?.reference
                ? `BRF ${voucher.info.paymentMethodObject.reference}`
                : '';
            const rmtFor = voucher.info?.for || voucher.info?.supplierName || '';
            return {
                isCount: true,
                text: [rmtRef, rmtFor ? `For: ${rmtFor}` : ''].filter(Boolean).join(' ')
            };
        }
        case documentTypesEnum.TRANSFER_RECEIPT:
        case documentTypesEnum.RECEIPT: {
            // Per TB: "For: {beneficiaryName}"
            const benefName = Array.isArray(voucher.info?.beneficiary)
                ? voucher.info.beneficiary[0]?.name || ''
                : voucher.info?.beneficiary || '';
            return {
                isCount: true,
                text: benefName ? `For: ${benefName}` : ''
            };
        }
        case documentTypesEnum.VOUCHER: {
            // Per TB: "PRQ {prqNumber} {supplierName} For: {beneficiary}"
            const vchSupplier = voucher.info?.supplierName || '';
            const coveredBy = voucher.info?.coveredBy;
            const coveredDoc = Array.isArray(coveredBy) ? coveredBy[0] : coveredBy;
            const prqRef = coveredDoc?.type === documentTypesEnum.PRQ ? `PRQ ${coveredDoc.number}` : '';
            const vchFor = voucher.info?.for || '';
            const vchText = [prqRef, vchSupplier, vchFor ? `For: ${vchFor}` : ''].filter(Boolean).join(' ');
            if (relatedServiceId) {
                return { isCount: false, text: vchText };
            }
            return { isCount: true, text: vchText };
        }
        case documentTypesEnum.CREDIT_DEBIT: {
            if (relatedServiceId) {
                return { isCount: false, text: '' };
            }
            return {
                isCount: true,
                text: '' // TODO: If issued for a general reduction, the account that is debited
            };
        }
        case documentTypesEnum.DEBIT_CREDIT: {
            if (relatedServiceId) {
                return { isCount: false, text: '' };
            }
            return {
                isCount: true,
                text: ''
            };
        }
        case documentTypesEnum.INVOICE: {
            const text = (() => {
                const coveredBy = voucher.info?.coveredBy;
                const coveredDoc = Array.isArray(coveredBy) ? coveredBy[0] : coveredBy;
                if (voucher.isPaid) {
                    return coveredDoc?.type
                        ? `Fully paid - ${coveredDoc.type} ${coveredDoc.number}`
                        : 'Fully paid';
                } else {
                    if (voucher.status === voucherStatusesEnum.RECONCILED) {
                        return coveredDoc?.type
                            ? `Reconciled - ${coveredDoc.type} ${coveredDoc.number}`
                            : 'Reconciled';
                    }
                    // Only show "Left to pay" when a real partial payment has been
                    // received. On a fresh unpaid invoice leftToPay equals netToRemit
                    // (which can be < amount due to commission) and labeling that as
                    // "Left to pay" misleadingly reads as partial payment — the
                    // status chip already communicates Pending.
                    const netToRemit = Number(voucher.info?.netToRemit) || 0;
                    const leftToPay = Number(voucher.info?.leftToPay) || 0;
                    if (Math.abs(leftToPay - netToRemit) < 0.01) return '';
                    return `Left to pay: ${taytanFormatNumberToString(leftToPay)}`;
                }
            })();
            return {
                isCount: true,
                text
            };
        }
        default: {
            return {
                isCount: true,
                text: ''
            };
        }
    }
};

export const getAbsoluteSignByDebitCredit = (props: {
    type: Voucher['type'];
    sourceId: Voucher['sourceId'];
    sourceType?: Voucher['sourceType'];
    travelFileId: TravelFile['_id'];
}) => {
    const { type, sourceId, sourceType, travelFileId } = props;
    // TODO: check if this logic is correct
    if (type === documentTypesEnum.CREDIT_DEBIT || type === documentTypesEnum.DEBIT_CREDIT) {
        if (sourceId && isSourceThisTravelFile({ sourceId, sourceType, travelFileId })) {
            return -1;
        }
    }
    return 1;
};

// All amounts are positive; credit/debit direction is handled by getVoucherTypeCreditOrDebit
const isVouherShoudBeInMinus = (_voucher: Voucher): boolean => false;

/**
 * A document's amount expressed in a TARGET currency — normally the travel
 * file's — for the accounting totals.
 *
 * WHY THIS EXISTS. Every totals path in the app carried its own copy of:
 *
 *     if (docCurrency === travelFileCurrency) converted = amount;
 *     else if (amountObj != null)             converted = amountObj;      // wrong
 *     else                                    converted = amount / rate;  // wrong
 *
 * `amountObj` is the amount in the **primary** (USD) currency and `info.rate`
 * is the primary↔local rate, so reaching for either on ANY currency mismatch
 * silently assumes the TRAVEL FILE is itself in the primary currency. It is,
 * for 149,590 of the 150,739 travel files in production — and for the other
 * 1,145 (ILS, EUR) a USD document contributed its DOLLAR figure to a SHEKEL
 * total. Across the imported ILS files that is roughly ₪2.78m of divergence
 * between what these totals sum and what `amountLocal` says.
 *
 * SAFETY. These totals feed the balance shown when a travel file is CLOSED, so
 * the floor here is "exactly what it did before", never a silent zero — a zero
 * would understate a balance rather than merely mis-state it. The legacy
 * arithmetic is therefore kept and used verbatim whenever the client's
 * local/primary currency ids are unavailable, or when a document offers nothing
 * to convert with (a EUR document on a USD file stores `amountObj = amount`,
 * a known limitation of `addNewDocument` that no stored field can recover).
 */
export const convertVoucherAmountToCurrency = (props: {
    info: unknown;
    targetCurrencyId: unknown;
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
    getConvertRate?: (from?: any, to?: any) => any;
}): number => {
    const { info, targetCurrencyId, localCurrencyId, primaryCurrencyId, getConvertRate } = props;
    const i = (info || {}) as Record<string, any>;

    if (localCurrencyId && primaryCurrencyId) {
        const resolved = resolveDocumentAmountForTable(i, {
            targetId: targetCurrencyId,
            localId: localCurrencyId,
            primaryId: primaryCurrencyId,
            getConvertRate
        });
        if (resolved !== undefined) return resolved;
    }

    // --- Legacy fallback, see SAFETY above. Unchanged arithmetic. ---
    const amount = Number(i.amount) || 0;
    // A zeroed-out document is 0 in every currency; some legacy rows kept a
    // stale non-zero `amountObj` after their amount was reset.
    if (!amount) return 0;
    const currencyId = i.currency?._id ?? i.currency;
    if (currencyId && String(currencyId) === String(targetCurrencyId)) return amount;
    if (i.amountObj != null) return Number(i.amountObj);
    return amount * (1 / (Number(i.rate) || 1));
};

/**
 * A non-amount money field (`commission`, `tax`) expressed in a TARGET
 * currency.
 *
 * Only `amount` has a stored counterpart in another currency, so these are
 * converted PROPORTIONALLY: whatever ratio the document's own amount resolves
 * at is applied to the field. That keeps a commission consistent with the
 * invoice it sits on, which reaching for `1 / info.rate` did not — that rate is
 * primary↔local and says nothing about a travel file in any third currency.
 */
export const convertVoucherFieldToCurrency = (props: {
    info: unknown;
    field: 'commission' | 'tax';
    targetCurrencyId: unknown;
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
    getConvertRate?: (from?: any, to?: any) => any;
}): number => {
    const { info, field, ...rest } = props;
    const i = (info || {}) as Record<string, any>;
    const raw = Number(i[field]) || 0;
    if (!raw) return 0;

    const amount = Number(i.amount) || 0;
    if (!amount) return raw;

    const convertedAmount = convertVoucherAmountToCurrency({ info: i, ...rest });
    return amount === convertedAmount ? raw : raw * (convertedAmount / amount);
};

export const getTotalVouchersAmountByVoucherRate = (props: {
    vouchers: Voucher[];
    travelFileCurrencyId: string;
    travelFileId?: string;
    /** The client's local (ILS) and primary (USD) currency ids. Without them
     *  the legacy arithmetic is used — see `convertVoucherAmountToCurrency`. */
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
}): number => {
    const { vouchers, travelFileCurrencyId, localCurrencyId, primaryCurrencyId } = props;
    return vouchers.reduce((acc, next) => {
        if (next.status === voucherStatusesEnum.CANCEL) {
            return acc;
        }
        const multiplyMinus: number = isVouherShoudBeInMinus(next) ? -1 : 1;
        const converted = convertVoucherAmountToCurrency({
            info: next.info,
            targetCurrencyId: travelFileCurrencyId,
            localCurrencyId,
            primaryCurrencyId
        });
        acc += getToFixedNumber(converted * multiplyMinus);

        return acc;
    }, 0);
};

export const getTotalVouchersAmountByVoucherRateDebit = (props: {
    vouchers: Voucher[];
    travelFileCurrencyId: string;
    travelFileId?: string;
    /** The client's local (ILS) and primary (USD) currency ids. Without them
     *  the legacy arithmetic is used — see `convertVoucherAmountToCurrency`. */
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
}): number => {
    const { vouchers, travelFileCurrencyId, localCurrencyId, primaryCurrencyId } = props;

    return vouchers.reduce((acc, next) => {
        if (next.status === voucherStatusesEnum.CANCEL) {
            return acc;
        }

        if (next.type !== documentTypesEnum.CREDIT_DEBIT && next.type !== documentTypesEnum.DEBIT_CREDIT) {
            return acc;
        }
        const multiplyMinus: number = isVouherShoudBeInMinus(next) ? -1 : 1;
        const converted = convertVoucherAmountToCurrency({
            info: next.info,
            targetCurrencyId: travelFileCurrencyId,
            localCurrencyId,
            primaryCurrencyId
        });
        acc += getToFixedNumber(converted * multiplyMinus);

        return acc;
    }, 0);
};

export const getTotalInvoicesAgentCommissionAmountByVoucherRate = (props: {
    vouchers: Voucher[]; // voucher of type invoice
    travelFileCurrencyId: string;
    localCurrencyId?: unknown;
    primaryCurrencyId?: unknown;
}): number => {
    const { vouchers, travelFileCurrencyId, localCurrencyId, primaryCurrencyId } = props;
    return vouchers.reduce((acc, next) => {
        if (next.status === voucherStatusesEnum.CANCEL) {
            return acc;
        }
        acc += getToFixedNumber(
            convertVoucherFieldToCurrency({
                info: next.info,
                field: 'commission',
                targetCurrencyId: travelFileCurrencyId,
                localCurrencyId,
                primaryCurrencyId
            })
        );
        return acc;
    }, 0);
};

export type VoucherStatuses = 'Reconciled' | 'Cancel' | 'Pending';
export type VoucherStatusesKeys = 'RECONCILED' | 'CANCEL' | 'PENDING';
export const voucherStatusesEnum: { [key in VoucherStatusesKeys]: VoucherStatuses } = {
    RECONCILED: 'Reconciled',
    CANCEL: 'Cancel',
    PENDING: 'Pending'
};
export const voucherStatusesOptions = Object.values(voucherStatusesEnum) as VoucherStatuses[];

/** Centralized status colors for document/voucher statuses — use everywhere for consistency */
export const voucherStatusColors: Record<
    VoucherStatuses,
    { bg: React.CSSProperties['backgroundColor']; color: React.CSSProperties['color'] }
> = {
    Pending: { bg: '#fff3e0', color: '#e65100' },
    Reconciled: { bg: '#e3f2fd', color: '#1565c0' },
    Cancel: { bg: '#fce4ec', color: '#c62828' }
};

/** Inline chip style per status — matches the CANCELLED chip in TF Accounting */
export const voucherStatusChipStyle: Record<string, React.CSSProperties> = {
    // Document statuses
    Pending: { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' },
    Reconciled: { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' },
    Cancel: { backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' },
    Cancelled: { backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' },
    // Accounting display labels
    Paid: { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
    'Partially Paid': { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' },
    // Receipt-specific labels (see receiptStatusDisplayLabel) — same palette as
    // the Pending/Reconciled statuses they stand in for.
    'Awaiting Deposit': { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' },
    Deposited: { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
    // Approval statuses
    Approved: { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
    Rejected: { backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }
};

/** Map raw status to display label — "Cancel" → "Cancelled" */
export const voucherStatusDisplayLabel: Record<string, string> = {
    Cancel: 'Cancelled'
};

export const isReceiptDocumentType = (type?: string): boolean =>
    type === documentTypesEnum.RECEIPT || type === documentTypesEnum.TRANSFER_RECEIPT;

export const isRefundDocumentType = (type?: string): boolean =>
    type === documentTypesEnum.REFUND || type === documentTypesEnum.EXTENDED_REFUND;

/**
 * On a receipt, the raw ledger statuses are jargon that reads as meaningless.
 * They do carry a precise meaning — whether the money has reached the bank —
 * which is exactly what drives the Cashier's deposit queue
 * (`isDeposited = false`, see pg.controller). Depositing a receipt sets both
 * `status = Reconciled` and `isDeposited = true`, so the mapping holds.
 *
 * Relabel for display only; the stored status is never changed.
 */
const receiptStatusDisplayLabel: Record<string, string> = {
    [voucherStatusesEnum.PENDING]: 'Awaiting Deposit',
    [voucherStatusesEnum.RECONCILED]: 'Deposited'
};

/**
 * Vouchers and Refunds carry no user-facing status until they are settled.
 * `Pending` on those types just means "issued and not yet covered", which read
 * as a problem during customer testing, so nothing is shown for it.
 *
 * Display only — the stored `Pending` is what drives Supplier/Travel Agent
 * Reconciliation, travel-file close, the dashboards and the ledger export, and
 * none of that changes.
 */
export const isUnlabelledDocumentStatus = (type?: string, status?: string): boolean =>
    status === voucherStatusesEnum.PENDING &&
    (type === documentTypesEnum.VOUCHER ||
        type === documentTypesEnum.REFUND ||
        type === documentTypesEnum.EXTENDED_REFUND);

/** Get the display status — relabels receipts, checks for approval overlay */
export const getDisplayStatus = (data: any): string => {
    const status = data?.status || '';
    if (isUnlabelledDocumentStatus(data?.type, status)) return '';
    if (isReceiptDocumentType(data?.type)) {
        return receiptStatusDisplayLabel[status] || voucherStatusDisplayLabel[status] || status;
    }
    if (status === 'Pending') {
        if (data?.rejectionReason) return 'Rejected';
        if (data?.approvedBy) return 'Approved';
    }
    return voucherStatusDisplayLabel[status] || status;
};

export type PaymentMethodTypes = 'Credit Card' | 'Cash' | 'Check' | 'Voucher' | 'Bank Transfer';
type PaymentMethodKeys = 'CREDIT_CARD' | 'CASH' | 'CHECK' | 'VOUCHER' | 'BANK_TRANSFER';
export const paymentMethodTypeEnum: { [key in PaymentMethodKeys]: PaymentMethodTypes } = {
    CREDIT_CARD: 'Credit Card',
    BANK_TRANSFER: 'Bank Transfer',
    CASH: 'Cash',
    CHECK: 'Check',
    VOUCHER: 'Voucher'
};
export const paymentMethodTypesOptions = Object.values(paymentMethodTypeEnum) as PaymentMethodTypes[];
export const paymentMethodTypesOptionsWithoutVoucher = paymentMethodTypesOptions.filter(
    (option) => option !== paymentMethodTypeEnum.VOUCHER
);

/**
 * A Transfer Receipt (TRC) is issued ONLY for a bank transfer — it is the
 * document that gets reconciled against the bank statement by its bank
 * reference (see the Transfer Book). Every other payment method produces a
 * plain Receipt (RCT).
 *
 * Use `getReceiptDocumentTypeByPaymentMethodType` rather than reading this map
 * directly when deciding the type of a document being created.
 */
export const receiptDocumentPreviewNameByPaymentMethodType = {
    [paymentMethodTypeEnum.BANK_TRANSFER]: documentTypesEnum.TRANSFER_RECEIPT,
    [paymentMethodTypeEnum.CREDIT_CARD]: documentTypesEnum.RECEIPT,
    [paymentMethodTypeEnum.CASH]: documentTypesEnum.RECEIPT,
    [paymentMethodTypeEnum.CHECK]: documentTypesEnum.RECEIPT,
    [paymentMethodTypeEnum.VOUCHER]: documentTypesEnum.RECEIPT
};

/**
 * Single source of truth for "which receipt document type does this payment
 * method produce". Bank Transfer → Transfer Receipt, everything else → Receipt.
 */
export const getReceiptDocumentTypeByPaymentMethodType = (
    paymentMethodType?: PaymentMethodTypes | string
): DocumentTypes =>
    paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER
        ? documentTypesEnum.TRANSFER_RECEIPT
        : documentTypesEnum.RECEIPT;

export const DEFAULT_GLOBAL_VAT_VALUE = 18; // Default fallback; prefer client.vatPercent when available

/** Get VAT percentage from client settings, falling back to DEFAULT_GLOBAL_VAT_VALUE */
export const getVatPercent = (client: any): number => {
    return Number(client?.vatPercent) || DEFAULT_GLOBAL_VAT_VALUE;
};

/**
 * VAT charged ON TOP of a Remittance amount ("Charge VAT / חייב מע״מ").
 *
 * The amount the user types stays what covers the supplier's open PRQ/voucher;
 * the VAT is added to it, so the payable total is `amount + vat`. It used to be
 * EXTRACTED from the amount (`gross − gross/(1+rate)`), which silently credited
 * the supplier less than the figure that was entered.
 *
 * Double entry: Dr supplier `amount`, Dr VAT-input (250032) `vat`, Cr bank the total.
 */
export const calcVatOnTop = (amount: number, vatPercent: number): number => {
    if (!(amount > 0) || !(vatPercent > 0)) return 0;
    return Math.round(amount * (vatPercent / 100) * 100) / 100;
};

/** Get VAT percentage from income types data (looks up "Comm. Plus VAT" rate) */
export const getVatPercentFromIncomeTypes = (incomeTypes: any[]): number => {
    if (!incomeTypes?.length) return DEFAULT_GLOBAL_VAT_VALUE;
    const commPlusVat = incomeTypes.find((it: any) => it.name === vatTypesEnum.COMM_PLUS_VAT);
    return Number(commPlusVat?.vat) || DEFAULT_GLOBAL_VAT_VALUE;
};

export type VatTypes =
    | ''
    | 'Markup inc. VAT'
    | 'Markup VAT zero rate'
    | 'Markup VAT Exempt'
    | 'Comm. Plus VAT'
    | 'Comm. inc. VAT'
    | 'Comm. VAT Zero'
    | 'Comm. VAT Exempt';
export type VatTypesKeys =
    | 'MARKUP_INC_VAT'
    | 'MARKUP_VAT_ZERO_RATE'
    | 'MARKUP_VAT_EXEMPT'
    | 'COMM_PLUS_VAT'
    | 'COMM_INC_VAT'
    | 'COMM_VAT_ZERO'
    | 'COMM_VAT_EXEMPT';

export const vatTypesEnum: { [key in VatTypesKeys]: VatTypes } = {
    MARKUP_INC_VAT: 'Markup inc. VAT',
    MARKUP_VAT_ZERO_RATE: 'Markup VAT zero rate',
    MARKUP_VAT_EXEMPT: 'Markup VAT Exempt',
    COMM_PLUS_VAT: 'Comm. Plus VAT',
    COMM_INC_VAT: 'Comm. inc. VAT',
    COMM_VAT_ZERO: 'Comm. VAT Zero',
    COMM_VAT_EXEMPT: 'Comm. VAT Exempt'
};
export const vatTypesOptions = Object.values(vatTypesEnum) as VatTypes[];

export const incomeTypesMapPercent: { [key in string]: { percent: number; includeVat?: boolean } } = {
    [vatTypesEnum.MARKUP_INC_VAT]: { percent: DEFAULT_GLOBAL_VAT_VALUE },
    [vatTypesEnum.MARKUP_VAT_ZERO_RATE]: { percent: 0 },
    [vatTypesEnum.MARKUP_VAT_EXEMPT]: { percent: 0 },
    [vatTypesEnum.COMM_PLUS_VAT]: { percent: DEFAULT_GLOBAL_VAT_VALUE },
    [vatTypesEnum.COMM_INC_VAT]: { percent: DEFAULT_GLOBAL_VAT_VALUE, includeVat: true },
    [vatTypesEnum.COMM_VAT_ZERO]: { percent: 0 },
    [vatTypesEnum.COMM_VAT_EXEMPT]: { percent: 0 }
};

/** Get income types map with client-specific VAT rate */
export const getIncomeTypesMapPercent = (
    vatPct: number
): { [key in string]: { percent: number; includeVat?: boolean } } => ({
    [vatTypesEnum.MARKUP_INC_VAT]: { percent: vatPct },
    [vatTypesEnum.MARKUP_VAT_ZERO_RATE]: { percent: 0 },
    [vatTypesEnum.MARKUP_VAT_EXEMPT]: { percent: 0 },
    [vatTypesEnum.COMM_PLUS_VAT]: { percent: vatPct },
    [vatTypesEnum.COMM_INC_VAT]: { percent: vatPct, includeVat: true },
    [vatTypesEnum.COMM_VAT_ZERO]: { percent: 0 },
    [vatTypesEnum.COMM_VAT_EXEMPT]: { percent: 0 }
});

export type AccountTypes =
    | 'Agent Commission Accounts'
    | 'Bank Accounts'
    | 'Discount Accounts'
    //| 'Fictitious Accounts'
    //| 'Fixed Accounts'
    | 'General Accounts'
    | 'Income Accounts';
export type AccountTypesKeys =
    | 'AGENT_COMM_ACCOUNTS'
    | 'BANK_ACCOUNTS'
    | 'DISCOUNT_ACCOUNTS'
    //| 'FICTITIOUS_ACCOUNTS'
    //| 'FIXED_ACCOUNTS'
    | 'GENERAL_ACCOUNTS'
    | 'INCOME_ACCOUNTS';
export const accountTypesEnum: { [key in AccountTypesKeys]: AccountTypes } = {
    AGENT_COMM_ACCOUNTS: 'Agent Commission Accounts',
    BANK_ACCOUNTS: 'Bank Accounts',
    DISCOUNT_ACCOUNTS: 'Discount Accounts',
    //FICTITIOUS_ACCOUNTS: 'Fictitious Accounts',
    //FIXED_ACCOUNTS: 'Fixed Accounts',
    GENERAL_ACCOUNTS: 'General Accounts',
    INCOME_ACCOUNTS: 'Income Accounts'
};
export const accountTypesOptions = Object.values(accountTypesEnum) as AccountTypes[];

/**
 * A numeric column value, or null.
 *
 * Forms send '' for an untouched field, which Postgres rejects for an integer
 * column, and `Number('')` is 0 — so "no link" would be indistinguishable from
 * a link to document zero. Both collapse to null. Objects are unwrapped by
 * `_id`, since pickers hand back whole rows.
 */
export const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(typeof value === 'object' ? (value as any)?._id : value);
    return Number.isFinite(n) ? n : null;
};
